import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { format, subDays, parseISO } from 'date-fns';

const PLATFORM_COLORS = {
  spotify: '#1DB954',
  apple_music: '#fc3c44',
  youtube_music: '#FF0000',
  tiktok_sound: '#69C9D0',
};

const PLATFORM_LABELS = {
  spotify: 'Spotify',
  apple_music: 'Apple Music',
  youtube_music: 'YouTube Music',
  tiktok_sound: 'TikTok Sound',
};

const CHART_COLORS = ['#7c3aed', '#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626'];

const StatCard = ({ icon, label, value, sub, colorClass = 'bg-primary/10 text-primary' }) => (
  <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-2">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClass}`}>
      <Icon name={icon} size={20} />
    </div>
    <p className="text-2xl font-bold text-foreground leading-tight">{value}</p>
    <div>
      <p className="text-sm font-medium text-foreground">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  </div>
);

const SectionHeader = ({ title, sub }) => (
  <div className="mb-4">
    <h2 className="text-base font-semibold text-foreground">{title}</h2>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const InfluencerClickAnalytics = () => {
  const { user } = useAuth();
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30); // days

  const fetchClicks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const since = subDays(new Date(), timeRange)?.toISOString();
      const { data, error: fetchError } = await supabase?.from('link_clicks')?.select('id, campaign_id, influencer_id, slug, platform, destination_url, country, device, clicked_at')?.gte('clicked_at', since)?.order('clicked_at', { ascending: false });

      if (fetchError) throw fetchError;
      setClicks(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load click data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchClicks();
  }, [fetchClicks]);

  // ── Derived metrics ──────────────────────────────────────────────────────────

  const totalClicks = clicks?.length;

  // Clicks by platform
  const byPlatform = Object.entries(
    clicks?.reduce((acc, c) => {
      const key = c?.platform || 'unknown';
      acc[key] = (acc?.[key] || 0) + 1;
      return acc;
    }, {})
  )?.map(([platform, count]) => ({
      platform,
      label: PLATFORM_LABELS?.[platform] || platform,
      count,
      fill: PLATFORM_COLORS?.[platform] || '#888',
    }))?.sort((a, b) => b?.count - a?.count);

  // Clicks by slug (top 10)
  const bySlug = Object.entries(
    clicks?.reduce((acc, c) => {
      const key = c?.slug || 'unknown';
      acc[key] = (acc?.[key] || 0) + 1;
      return acc;
    }, {})
  )?.map(([slug, count]) => ({ slug, count }))?.sort((a, b) => b?.count - a?.count)?.slice(0, 10);

  // Clicks by influencer_id (top 10)
  const byInfluencer = Object.entries(
    clicks?.reduce((acc, c) => {
      const key = c?.influencer_id || 'unknown';
      acc[key] = (acc?.[key] || 0) + 1;
      return acc;
    }, {})
  )?.map(([influencer_id, count]) => ({
      influencer_id,
      label: influencer_id === 'unknown' ? 'Unknown' : `ID: ${influencer_id}`,
      count,
    }))?.sort((a, b) => b?.count - a?.count)?.slice(0, 10);

  // Clicks over time (daily buckets)
  const byDay = (() => {
    const buckets = {};
    for (let i = timeRange - 1; i >= 0; i--) {
      const day = format(subDays(new Date(), i), 'MMM d');
      buckets[day] = 0;
    }
    clicks?.forEach((c) => {
      if (c?.clicked_at) {
        const day = format(parseISO(c?.clicked_at), 'MMM d');
        if (buckets?.hasOwnProperty(day)) {
          buckets[day] += 1;
        }
      }
    });
    return Object.entries(buckets)?.map(([date, count]) => ({ date, count }));
  })();

  const topPlatform = byPlatform?.[0];

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground text-sm">Loading click analytics…</p>
          </div>
        </div>
        <MobileNav />
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center max-w-sm">
            <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={24} className="text-destructive" />
            </div>
            <p className="text-foreground font-medium mb-1">Failed to load data</p>
            <p className="text-muted-foreground text-sm mb-4">{error}</p>
            <button
              onClick={fetchClicks}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
        <MobileNav />
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 lg:pb-8">

        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">Click Analytics</h1>
            <p className="text-muted-foreground text-sm">
              Live data from <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">public.link_clicks</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 14, 30, 90]?.map((d) => (
              <button
                key={d}
                onClick={() => setTimeRange(d)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === d
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {d}d
              </button>
            ))}
            <button
              onClick={fetchClicks}
              className="p-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
              title="Refresh"
            >
              <Icon name="RefreshCw" size={16} />
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon="MousePointerClick"
            label="Total Clicks"
            value={totalClicks?.toLocaleString()}
            sub={`Last ${timeRange} days`}
            colorClass="bg-primary/10 text-primary"
          />
          <StatCard
            icon="Layers"
            label="Unique Slugs"
            value={bySlug?.length?.toLocaleString()}
            sub="Distinct links"
            colorClass="bg-blue-500/10 text-blue-500"
          />
          <StatCard
            icon="Users"
            label="Influencers"
            value={byInfluencer?.filter(i => i?.influencer_id !== 'unknown')?.length?.toLocaleString()}
            sub="With tracked clicks"
            colorClass="bg-purple-500/10 text-purple-500"
          />
          <StatCard
            icon="TrendingUp"
            label="Top Platform"
            value={topPlatform ? topPlatform?.label : '—'}
            sub={topPlatform ? `${topPlatform?.count} clicks` : 'No data yet'}
            colorClass="bg-green-500/10 text-green-500"
          />
        </div>

        {/* Clicks over time */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <SectionHeader title="Clicks Over Time" sub={`Daily click volume — last ${timeRange} days`} />
          {byDay?.every(d => d?.count === 0) ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
              No click data in this period
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={byDay} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  interval={timeRange <= 14 ? 0 : Math.floor(timeRange / 7)}
                />
                <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                  labelStyle={{ color: 'var(--foreground)', fontWeight: 600 }}
                  formatter={(v) => [v, 'Clicks']}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Platform + Slug row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

          {/* Clicks by platform */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <SectionHeader title="Clicks by Platform" sub="Distribution across streaming platforms" />
            {byPlatform?.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No data yet</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={byPlatform}
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      dataKey="count"
                      nameKey="label"
                      label={({ label, percent }) =>
                        percent > 0.05 ? `${label} ${(percent * 100)?.toFixed(0)}%` : ''
                      }
                      labelLine={false}
                    >
                      {byPlatform?.map((entry, i) => (
                        <Cell key={i} fill={entry?.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                      formatter={(v, name) => [v, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-2">
                  {byPlatform?.map((p) => (
                    <div key={p?.platform} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: p?.fill }} />
                        <span className="text-foreground">{p?.label}</span>
                      </div>
                      <span className="font-semibold text-foreground">{p?.count?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Clicks by slug */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <SectionHeader title="Clicks by Slug" sub="Top 10 performing links" />
            {bySlug?.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={bySlug?.length * 36 + 20}>
                <BarChart
                  data={bySlug}
                  layout="vertical"
                  margin={{ top: 0, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="slug"
                    tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                    width={90}
                    tickFormatter={(v) => v?.length > 12 ? v?.slice(0, 12) + '…' : v}
                  />
                  <Tooltip
                    contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                    formatter={(v) => [v, 'Clicks']}
                  />
                  <Bar dataKey="count" fill="var(--primary)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Clicks by influencer_id */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <SectionHeader title="Clicks by Influencer" sub="Top 10 influencers by click volume" />
          {byInfluencer?.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byInfluencer} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  tickFormatter={(v) => v?.length > 14 ? v?.slice(0, 14) + '…' : v}
                />
                <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                  labelStyle={{ color: 'var(--foreground)' }}
                  formatter={(v) => [v, 'Clicks']}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {byInfluencer?.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS?.[i % CHART_COLORS?.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Recent clicks table */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <SectionHeader title="Recent Clicks" sub="Latest 50 click events" />
          {clicks?.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
              No clicks recorded yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {['Slug', 'Platform', 'Influencer ID', 'Device', 'Country', 'Clicked At']?.map((h) => (
                      <th key={h} className="text-left py-2 px-3 text-xs font-medium text-muted-foreground whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {clicks?.slice(0, 50)?.map((c) => (
                    <tr key={c?.id} className="hover:bg-muted/30 transition-colors">
                      <td className="py-2 px-3 font-mono text-xs text-foreground">{c?.slug || '—'}</td>
                      <td className="py-2 px-3">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            background: (PLATFORM_COLORS?.[c?.platform] || '#888') + '20',
                            color: PLATFORM_COLORS?.[c?.platform] || 'var(--muted-foreground)',
                          }}
                        >
                          {PLATFORM_LABELS?.[c?.platform] || c?.platform || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-muted-foreground font-mono text-xs">
                        {c?.influencer_id ? String(c?.influencer_id)?.slice(0, 8) + '…' : '—'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground capitalize">{c?.device || '—'}</td>
                      <td className="py-2 px-3 text-muted-foreground">{c?.country || '—'}</td>
                      <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">
                        {c?.clicked_at ? format(parseISO(c?.clicked_at), 'MMM d, HH:mm') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </main>
      <MobileNav />
    </div>
  );
};

export default InfluencerClickAnalytics;
